import posthog from 'posthog-js';
import type { PostHog, Properties } from 'posthog-js';
import { config } from './config';

export type AnalyticsEventName =
  | '$pageview'
  | 'user_signed_up'
  | 'onboarding_started'
  | 'onboarding_step_viewed'
  | 'household_created'
  | 'email_review_opened'
  | 'review_item_approved'
  | 'review_item_dismissed'
  | 'review_v0_opened'
  | 'review_item_set_aside'
  | 'review_item_set_aside_undone'
  | 'review_confirm_all'
  | 'review_item_details_opened'
  | 'settings_section_viewed';

type AnalyticsPropertyValue = string | number | boolean | null | undefined;
export type AnalyticsProperties = Record<string, AnalyticsPropertyValue>;

export interface AnalyticsEventDefinition {
  name: AnalyticsEventName;
  trigger: string;
  allowedProperties: readonly string[];
}

export const POSTHOG_EVENT_CATALOG: readonly AnalyticsEventDefinition[] = [
  {
    name: '$pageview',
    trigger: 'React Router location changes',
    allowedProperties: ['path', 'route', 'section'],
  },
  {
    name: 'user_signed_up',
    trigger: 'First authenticated app session for a Supabase user',
    allowedProperties: ['source'],
  },
  {
    name: 'onboarding_started',
    trigger: 'User starts onboarding',
    allowedProperties: ['entry_route'],
  },
  {
    name: 'onboarding_step_viewed',
    trigger: 'Onboarding step changes',
    allowedProperties: ['step_key'],
  },
  {
    name: 'household_created',
    trigger: 'Household creation succeeds',
    allowedProperties: ['source'],
  },
  {
    name: 'email_review_opened',
    trigger: 'Review modal opens',
    allowedProperties: ['source', 'status'],
  },
  {
    name: 'review_item_approved',
    trigger: 'Review item approved',
    allowedProperties: ['item_type'],
  },
  {
    name: 'review_item_dismissed',
    trigger: 'Review item dismissed',
    allowedProperties: ['item_type'],
  },
  {
    name: 'review_v0_opened',
    trigger: 'Review v0 list first loads (VITE_ENABLE_REVIEW_V0)',
    allowedProperties: ['count'],
  },
  {
    name: 'review_item_set_aside',
    trigger: "Review v0 card action: Don't add / Don't change / Keep event",
    allowedProperties: ['kind'],
  },
  {
    name: 'review_item_set_aside_undone',
    trigger: 'Review v0 Undo of a card action succeeded',
    allowedProperties: ['kind'],
  },
  {
    name: 'review_confirm_all',
    trigger: 'Review v0 Confirm all wrote a batch',
    allowedProperties: ['items', 'emails'],
  },
  {
    name: 'review_item_details_opened',
    trigger: 'Review v0 details sheet opened from a card or its source line',
    allowedProperties: ['kind', 'section'],
  },
  {
    name: 'settings_section_viewed',
    trigger: 'Settings subpage opens',
    allowedProperties: ['section'],
  },
];

const EVENT_PROPERTY_ALLOWLIST = POSTHOG_EVENT_CATALOG.reduce<
  Record<AnalyticsEventName, readonly string[]>
>(
  (acc, event) => ({
    ...acc,
    [event.name]: event.allowedProperties,
  }),
  {
    $pageview: [],
    user_signed_up: [],
    onboarding_started: [],
    onboarding_step_viewed: [],
    household_created: [],
    email_review_opened: [],
    review_item_approved: [],
    review_item_dismissed: [],
    review_v0_opened: [],
    review_item_set_aside: [],
    review_item_set_aside_undone: [],
    review_confirm_all: [],
    review_item_details_opened: [],
    settings_section_viewed: [],
  },
);

let analyticsClient: PostHog | null = null;
let initialized = false;
let lastIdentifiedUserId: string | null = null;

function getOnceKey(eventName: AnalyticsEventName, userId: string): string {
  return `yaruta_analytics_${eventName}_${userId}`;
}

function hasCapturedOnce(
  eventName: AnalyticsEventName,
  userId: string,
): boolean {
  try {
    return window.localStorage.getItem(getOnceKey(eventName, userId)) === '1';
  } catch {
    return false;
  }
}

function markCapturedOnce(eventName: AnalyticsEventName, userId: string): void {
  try {
    window.localStorage.setItem(getOnceKey(eventName, userId), '1');
  } catch {
    /* localStorage can be unavailable in private contexts */
  }
}

function maskToken(token: string | undefined): string | null {
  if (!token) return null;
  if (token.length <= 10) return `${token.slice(0, 3)}...`;
  return `${token.slice(0, 6)}...${token.slice(-4)}`;
}

function getDashboardUrl(): string | null {
  if (!config.posthogAppHost || !config.posthogProjectId) return null;
  return `${config.posthogAppHost.replace(/\/$/, '')}/project/${config.posthogProjectId}`;
}

function shouldInitializePostHog(): boolean {
  return Boolean(
    config.enableAnalytics && config.posthogToken && config.posthogHost,
  );
}

export function initializeAnalytics(): PostHog | null {
  if (initialized) return analyticsClient;

  initialized = true;
  if (!shouldInitializePostHog()) return null;

  const token = config.posthogToken;
  if (!token) return null;

  posthog.init(token, {
    api_host: config.posthogHost,
    defaults: '2026-01-30',
    autocapture: true,
    capture_pageview: false,
    disable_session_recording: !config.enablePostHogReplay,
    enable_recording_console_log: false,
    mask_all_text: true,
    mask_all_element_attributes: true,
    session_recording: {
      blockClass: 'ph-block-replay',
      ignoreClass: 'ph-ignore-input',
      maskTextClass: 'ph-mask',
      maskAllInputs: true,
      recordHeaders: false,
      recordBody: false,
    },
    opt_out_capturing_by_default: false,
    loaded: (client) => {
      if (config.appEnv !== 'production') client.debug();
    },
  });

  analyticsClient = posthog;
  return analyticsClient;
}

export function getAnalyticsClient(): PostHog | null {
  return analyticsClient;
}

export function sanitizeAnalyticsProperties(
  eventName: AnalyticsEventName,
  properties: AnalyticsProperties = {},
): Properties {
  const allowedKeys = new Set(EVENT_PROPERTY_ALLOWLIST[eventName]);
  return Object.entries(properties).reduce<Properties>((safe, [key, value]) => {
    if (!allowedKeys.has(key) || value === undefined || value === null) {
      return safe;
    }
    return { ...safe, [key]: value };
  }, {});
}

export function captureEvent(
  eventName: AnalyticsEventName,
  properties?: AnalyticsProperties,
): boolean {
  const client = getAnalyticsClient();
  if (!client) return false;

  client.capture(eventName, sanitizeAnalyticsProperties(eventName, properties));
  return true;
}

export function captureEventOnceForUser(
  eventName: AnalyticsEventName,
  userId: string,
  properties?: AnalyticsProperties,
): void {
  if (hasCapturedOnce(eventName, userId)) return;
  if (captureEvent(eventName, properties)) {
    markCapturedOnce(eventName, userId);
  }
}

export function identifyAnalyticsUser(userId: string, hasEmail: boolean): void {
  const client = getAnalyticsClient();
  if (!client || lastIdentifiedUserId === userId) return;

  client.identify(userId, {
    app_env: config.appEnv,
    has_email: hasEmail,
  });
  lastIdentifiedUserId = userId;
}

export function resetAnalyticsUser(): void {
  const client = getAnalyticsClient();
  if (!client) return;

  client.reset();
  lastIdentifiedUserId = null;
}

export function getPostHogAdminInfo() {
  const configured = Boolean(config.posthogToken && config.posthogHost);
  const dashboardUrl = getDashboardUrl();

  return {
    enabled: config.enableAnalytics,
    configured,
    initialized,
    projectId: config.posthogProjectId ?? null,
    host: config.posthogHost ?? null,
    appHost: config.posthogAppHost ?? null,
    dashboardUrl,
    tokenPreview: maskToken(config.posthogToken),
    sessionRecordingEnabled: config.enablePostHogReplay,
    autocaptureEnabled: true,
    manualPageviews: true,
    eventCatalog: POSTHOG_EVENT_CATALOG,
  };
}
